import { useEffect, useMemo, useState } from 'react';
import { Braces, X } from 'lucide-react';
import { customAdminApi } from '../../../services/api';
import type { CustomProduct, CustomProductDefinition, CustomProductRevision } from '../../../types/custom';
import { customErrorMessage } from '../../../utils/customUi';

const GENERIC_DEFINITION: CustomProductDefinition = {
  schemaVersion: 1,
  artifactContract: { format: 'zip', requiredPaths: ['server.bin'] },
  build: {
    dockerfile: 'FROM ubuntu:22.04\nWORKDIR /app\nCOPY . /app\nRUN chmod +x /app/server.bin\n',
  },
  launch: {
    executable: './server.bin',
    fixedArgs: [],
    parameters: [],
    environment: [],
    ports: [{
      key: 'service',
      label: 'Service port',
      containerPort: 27015,
      protocol: 'udp',
      hostPort: { mode: 'dynamic', min: 25000, max: 35000 },
    }],
    volumes: [],
    workingDirectory: '/app',
    runtimeFilesRoot: '/app',
    restartPolicy: 'unless-stopped',
    stopTimeoutSeconds: 60,
  },
  resourcePolicy: {
    defaultProfile: 'standard',
    profiles: [{
      key: 'standard',
      label: 'Standard',
      cpuCores: 1,
      memoryBytes: 1073741824,
      storageBytes: 10737418240,
    }],
  },
};

export function ProductMetadataModal({
  product,
  onClose,
  onSaved,
}: {
  product?: CustomProduct;
  onClose: () => void;
  onSaved: (product: CustomProduct) => void;
}) {
  const [key, setKey] = useState(product?.key || '');
  const [displayName, setDisplayName] = useState(product?.displayName || '');
  const [description, setDescription] = useState(product?.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const result = product
        ? await customAdminApi.updateProduct(product.id, { displayName, description })
        : await customAdminApi.createProduct({ key, displayName, ...(description ? { description } : {}) });
      onSaved(result.product);
    } catch (caught) {
      setError(customErrorMessage(caught, 'The product could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return <div className="modal-overlay" onClick={onClose}>
    <form className="modal-content max-w-2xl" onSubmit={submit} onClick={event => event.stopPropagation()}>
      <div className="modal-header"><div><h2 className="modal-title">{product ? 'Edit product' : 'Create custom product'}</h2><p className="text-xs text-gray-500 mt-1">Metadata stays editable; runtime definitions are immutable revisions.</p></div><button type="button" className="btn-ghost p-2" onClick={onClose}><X className="w-4 h-4" /></button></div>
      <div className="modal-body space-y-4">
        {error && <div className="p-3 rounded border border-red-500/30 bg-red-500/10 text-sm text-red-300">{error}</div>}
        <label className="block text-sm text-gray-400">Product key<input className="input mt-1" value={key} disabled={Boolean(product)} required pattern="[a-z0-9][a-z0-9-]*" placeholder="my-custom-product" onChange={event => setKey(event.target.value)} /><span className="text-xs text-gray-600">Lowercase immutable slug.</span></label>
        <label className="block text-sm text-gray-400">Display name<input className="input mt-1" value={displayName} required maxLength={128} onChange={event => setDisplayName(event.target.value)} /></label>
        <label className="block text-sm text-gray-400">Description<textarea className="input mt-1 h-24 resize-y" value={description} maxLength={4096} onChange={event => setDescription(event.target.value)} /></label>
      </div>
      <div className="modal-footer"><button type="button" className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save product'}</button></div>
    </form>
  </div>;
}

export function RevisionEditorModal({
  product,
  baseRevision,
  onClose,
  onSaved,
}: {
  product: CustomProduct;
  baseRevision?: CustomProductRevision;
  onClose: () => void;
  onSaved: (revision: CustomProductRevision) => void;
}) {
  const initial = useMemo(
    () => JSON.stringify(baseRevision?.definition || GENERIC_DEFINITION, null, 2),
    [baseRevision],
  );
  const [source, setSource] = useState(initial);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => setSource(initial), [initial]);

  const parseDefinition = (): CustomProductDefinition | null => {
    try {
      const parsed = JSON.parse(source);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Definition must be a JSON object.');
      setError('');
      return parsed as CustomProductDefinition;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Definition is not valid JSON.');
      return null;
    }
  };

  const submit = async () => {
    const definition = parseDefinition();
    if (!definition) return;
    setSaving(true);
    try {
      const result = await customAdminApi.createRevision(product.id, definition);
      onSaved(result.revision);
    } catch (caught) {
      setError(customErrorMessage(caught, 'The revision could not be created.'));
    } finally {
      setSaving(false);
    }
  };

  return <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content max-w-5xl" onClick={event => event.stopPropagation()}>
      <div className="modal-header"><div><h2 className="modal-title flex items-center gap-2"><Braces className="w-5 h-5 text-neon-400" /> New immutable revision</h2><p className="text-xs text-gray-500 mt-1">{product.displayName} · generic declarative schema v1 · no shell interpolation</p></div><button className="btn-ghost p-2" onClick={onClose}><X className="w-4 h-4" /></button></div>
      <div className="modal-body space-y-3">
        {error && <div className="p-3 rounded border border-red-500/30 bg-red-500/10 text-sm text-red-300 whitespace-pre-wrap">{error}</div>}
        <div className="flex flex-wrap gap-3 text-xs text-gray-500"><span>Artifact paths, Dockerfile, typed parameters, ports, volumes and resource profiles are all revisioned.</span><button className="text-neon-400 hover:underline" onClick={parseDefinition}>Validate JSON syntax</button></div>
        <textarea aria-label="Custom product definition JSON" className="input h-[58vh] resize-y font-mono text-xs leading-5" spellCheck={false} value={source} onChange={event => setSource(event.target.value)} />
      </div>
      <div className="modal-footer"><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" disabled={saving} onClick={submit}>{saving ? 'Creating…' : `Create revision ${product.latestRevision + 1}`}</button></div>
    </div>
  </div>;
}
